import { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, PermissionGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';


// ----------------------------------------------------------------------

// OVERVIEW
const AppPage = lazy(() => import('src/pages/dashboard/app'));
const AiUsersPage = lazy(() => import('src/pages/dashboard/ai/list'));
const NotificationsListPage = lazy(() => import('src/pages/dashboard/notifications/list'));
const NotificationDetailsPage = lazy(() => import('src/pages/dashboard/notifications/details'));
// USER MANAGEMENT
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserAccount = lazy(() => import('src/pages/dashboard/user/account'));
// JOB MANAGEMENT
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
// APPLICATION MANAGEMENT
const ApplicationListPage = lazy(() => import('src/pages/dashboard/application/list'));
const ApplicationDetailsView = lazy(() => import('src/sections/application/view/application-details-view'));
// TALENT MANAGEMENT
const TalentListPage = lazy(() => import('src/pages/dashboard/talent/list'));
const TalentHirePage = lazy(() => import('src/pages/dashboard/talent/hired'));
const TalentCreatePage = lazy(() => import('src/pages/dashboard/talent/new'));
const TalentEditPage = lazy(() => import('src/pages/dashboard/talent/edit'));
const TalentDetailsPage = lazy(() => import('src/pages/dashboard/talent/details'));
const TalentProfilePage = lazy(() => import('src/pages/dashboard/talent/profile'));
// AGENCY MANAGEMENT
const AgencyListPage = lazy(() => import('src/pages/dashboard/agency/list'));
const AgencyCreatePage = lazy(() => import('src/pages/dashboard/agency/new'));
const AgencyEditPage = lazy(() => import('src/pages/dashboard/agency/edit'));
// ORGANIZATION MANAGEMENT
const OrganizationListPage = lazy(() => import('src/pages/dashboard/organization/list'));
const OrganizationCreatePage = lazy(() => import('src/pages/dashboard/organization/new'));
const OrganizationEditPage = lazy(() => import('src/pages/dashboard/organization/edit'));
// TALENT TYPES MANAGEMENT
const TalentTypesListPage = lazy(() => import('src/pages/dashboard/talent-types/list'));
const TalentOffersListPage = lazy(() => import('src/pages/dashboard/talent-offers/list'));
const ServiceLeadsPage = lazy(() => import('src/pages/dashboard/service-leads'));
const FilmMonitorPage = lazy(() => import('src/pages/dashboard/film-monitor'));
const VoicesPage = lazy(() => import('src/pages/dashboard/voices'));
const RepresentationAgreementsListPage = lazy(() => import('src/pages/dashboard/representation-agreements/list'));
const RepresentationAgreementDetailPage = lazy(() => import('src/pages/dashboard/representation-agreements/detail'));
const ChatsListPage = lazy(() => import('src/pages/dashboard/chats/list'));
const TalentTypesEditPage = lazy(() => import('src/pages/dashboard/talent-types/edit'));
const TalentTypesNewPage = lazy(() => import('src/pages/dashboard/talent-types/new'));
// LEADS MANAGEMENT
const LeadsListPage = lazy(() => import('src/pages/dashboard/lead-list'));
const LeadNewPage = lazy(() => import('src/pages/dashboard/lead/new'));
const LeadDetailsPage = lazy(() => import('src/pages/dashboard/lead/details'));
const LeadEditPage = lazy(() => import('src/pages/dashboard/lead/edit'));
// LOCATION MANAGEMENT
const LocationListPage = lazy(() => import('src/pages/dashboard/location/list'));
const LocationDetailsPage = lazy(() => import('src/pages/dashboard/location/details'));
// PROPOSALS
const ProposalListPage = lazy(() => import('src/pages/dashboard/proposals/list'));
const ProposalDetailsPage = lazy(() => import('src/pages/dashboard/proposals/details'));
// EQUIPMENT MANAGEMENT
const EquipmentListPage = lazy(() => import('src/pages/dashboard/equipment/list'));
const EquipmentDetailsPage = lazy(() => import('src/pages/dashboard/equipment/details'));
// AUDITION MANAGEMENT
const AuditionsListPage = lazy(() => import('src/pages/dashboard/auditions/list'));
const AuditionDetailsPage = lazy(() => import('src/pages/dashboard/auditions/details'));
// CLIENT MANAGEMENT
const ClientsListPage = lazy(() => import('src/pages/dashboard/clients/list'));
const ClientDetailsPage = lazy(() => import('src/pages/dashboard/clients/details'));
// GEO MANAGEMENT
const GeoCountriesPage = lazy(() => import('src/pages/dashboard/geo/countries'));
const GeoStatesPage = lazy(() => import('src/pages/dashboard/geo/states'));
const GeoCitiesPage = lazy(() => import('src/pages/dashboard/geo/cities'));
const GeoTaxRatesPage = lazy(() => import('src/pages/dashboard/geo/tax-rates'));
// SERVICES MANAGEMENT
const ServicesListPage = lazy(() => import('src/pages/dashboard/services/list'));
const ServicesNewPage = lazy(() => import('src/pages/dashboard/services/new'));
// LANGUAGES MANAGEMENT
const LanguagesListPage = lazy(() => import('src/pages/dashboard/languages/list'));
// CONTACT FORMS
const ContactFormsListPage = lazy(() => import('src/pages/dashboard/contact-forms/list'));
const ContactFormDetailsPage = lazy(() => import('src/pages/dashboard/contact-forms/details'));
const TestimonialListPage = lazy(() => import('src/pages/dashboard/testimonials/list'));
const TestimonialNewPage = lazy(() => import('src/pages/dashboard/testimonials/new'));
const TestimonialEditPage = lazy(() => import('src/pages/dashboard/testimonials/edit'));
const ContractListPage = lazy(() => import('src/pages/dashboard/contracts/list'));
const ContractDetailsPage = lazy(() => import('src/pages/dashboard/contracts/details'));
const SharedMediaListPage = lazy(() => import('src/pages/dashboard/shared-media/list'));
const AccessRequestsListPage = lazy(() => import('src/pages/dashboard/access-requests/list'));
const ProjectListPage = lazy(() => import('src/pages/dashboard/projects/list'));
const ProjectDetailsPage = lazy(() => import('src/pages/dashboard/projects/details'));
// CAMPAIGNS
const EmailCampaignPage = lazy(() => import('src/pages/dashboard/campaign/email'));
const WhatsappCampaignPage = lazy(() => import('src/pages/dashboard/campaign/whatsapp'));
const CampaignLeadsPage = lazy(() => import('src/pages/dashboard/campaign/leads'));
const AccountDeletionsPage = lazy(() => import('src/pages/dashboard/account-deletions/list'));
// AI LIKENESS
const LikenessOptInsPage = lazy(() => import('src/pages/dashboard/likeness/opt-ins'));
const LikenessTermsPage = lazy(() => import('src/pages/dashboard/likeness/terms'));
const LikenessPackagePage = lazy(() => import('src/pages/dashboard/likeness/package'));
// BOOKINGS
const BookingsListPage = lazy(() => import('src/pages/dashboard/bookings/list'));
// JOB LEADS
const JobLeadsListPage = lazy(() => import('src/pages/dashboard/job-leads/list'));
const JobLeadDetailsPage = lazy(() => import('src/pages/dashboard/job-leads/details'));
const BrightDataScrapeJobsListPage = lazy(() => import('src/pages/dashboard/job-leads/bright-data'));
  // ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <AppPage />, index: true },
      { path: 'summary',
        children: [
          { element: <AppPage />, index: true },
          { path: 'app', element: <AppPage /> },
        ]
      },
      {
        path: 'ai',
        children: [
          { element: <PermissionGuard permission="ai"><AiUsersPage /></PermissionGuard>, index: true },
        ],
      },
      {
        path: 'notifications',
        children: [
          { element: <NotificationsListPage />, index: true },
          { path: ':id', element: <NotificationDetailsPage /> },
        ],
      },
      { path: 'user',
        children: [
          { element: <PermissionGuard permission="users"><UserListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path:'new', element: <PermissionGuard permission="users"><UserCreatePage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="users"><UserEditPage /></PermissionGuard> },
          { path: 'account', element: <UserAccount /> },
        ]
      },
      { path: 'job',
        children: [
          { element: <PermissionGuard permission="jobs"><JobListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path:'new', element: <PermissionGuard permission="jobs"><JobCreatePage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="jobs"><JobEditPage /></PermissionGuard> },
          { path: ':id', element: <PermissionGuard permission="jobs"><JobDetailsPage /></PermissionGuard> }
        ]
      },
      { path: 'application',
        children: [
          { element: <PermissionGuard permission="jobs"><ApplicationListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':jobId/:candidateShortUrl', element: <PermissionGuard permission="jobs"><ApplicationDetailsView /></PermissionGuard> },
        ]
      },
      { path: 'talent',
        children: [
          { element: <PermissionGuard permission="profiles"><TalentListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path:'hired', element: <PermissionGuard permission="profiles"><TalentHirePage /></PermissionGuard> },
          { path:'new', element: <PermissionGuard permission="profiles"><TalentCreatePage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="profiles"><TalentEditPage /></PermissionGuard> },
          { path: ':short_url', element: <PermissionGuard permission="profiles"><TalentProfilePage /></PermissionGuard> },
          { path: ':id', element: <PermissionGuard permission="profiles"><TalentDetailsPage /></PermissionGuard> }
        ]
      },
      {
        path: 'agency',
        children: [
          { element: <PermissionGuard permission="agencies"><AgencyListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path:'new', element: <PermissionGuard permission="agencies"><AgencyCreatePage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="agencies"><AgencyEditPage /></PermissionGuard> },
        ]
      },
      {
        path: 'organization',
        children: [
          { element: <PermissionGuard permission="organizations"><OrganizationListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path:'new', element: <PermissionGuard permission="organizations"><OrganizationCreatePage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="organizations"><OrganizationEditPage /></PermissionGuard> },
        ]
      },
      {
        path: 'talent-types',
        children: [
          { element: <PermissionGuard permission="talentTypes"><TalentTypesListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':id/edit', element: <PermissionGuard permission="talentTypes"><TalentTypesEditPage /></PermissionGuard> },
          { path: 'new', element: <PermissionGuard permission="talentTypes"><TalentTypesNewPage /></PermissionGuard> },
        ]
      },
      // Read-only talent offers listing (audit / tracking surface). Gated
      // behind a dedicated `talentOffers` permission so it can be scoped
      // independently of the general users/talents permission.
      {
        path: 'talent-offers',
        children: [
          { element: <PermissionGuard permission="talentOffers"><TalentOffersListPage /></PermissionGuard>, index: true },
        ]
      },
      // Platform-wide Services & Leads overview — who is enquiring about
      // what, across every talent. Distinct from the per-talent Services &
      // Leads tab on the talent edit page.
      {
        path: 'service-leads',
        children: [
          { element: <PermissionGuard permission="serviceLeads"><ServiceLeadsPage /></PermissionGuard>, index: true },
        ]
      },
      // Platform-wide, read-only Film module monitoring — mirrors client-media's
      // /dashboard/film (Scripts/Assets/Schedule/Call Sheets workspace) but
      // across every company, for audit/tracking rather than editing.
      {
        path: 'film-monitor',
        children: [
          { element: <PermissionGuard permission="film"><FilmMonitorPage /></PermissionGuard>, index: true },
        ]
      },
      // Platform-wide voice directory — reuses the public /voices search
      // logic server-side, plus contact info and zero-demo signups.
      {
        path: 'voices',
        children: [
          { element: <PermissionGuard permission="voices"><VoicesPage /></PermissionGuard>, index: true },
        ]
      },
      {
        path: 'representation-agreements',
        children: [
          { element: <PermissionGuard permission="representationAgreements"><RepresentationAgreementsListPage /></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="representationAgreements"><RepresentationAgreementDetailPage /></PermissionGuard> },
        ]
      },
      // Read-only chat monitor (1:1 messages from the CRM). Dedicated
      // `chats` permission — see admin edit UI to grant per user/role.
      {
        path: 'chats',
        children: [
          { element: <PermissionGuard permission="chats"><ChatsListPage /></PermissionGuard>, index: true },
        ]
      },
      {
        path: 'leads',
        // NOTE: :id/edit must be registered alongside :id — react-router
        // matches the more specific path automatically.
        children: [
          { element: <PermissionGuard permission="leads"><LeadsListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: 'list', element: <PermissionGuard permission="leads"><LeadsListPage /></PermissionGuard> },
          { path: 'new', element: <PermissionGuard permission="leads"><LeadNewPage /></PermissionGuard> },
          { path: ':id', element: <PermissionGuard permission="leads"><LeadDetailsPage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="leads"><LeadEditPage /></PermissionGuard> },
        ]
      },
      {
        path: 'location',
        children: [
          { element: <PermissionGuard permission="locations"><LocationListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="locations"><LocationDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'proposals',
        children: [
          { element: <PermissionGuard permission="proposals"><ProposalListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: 'list', element: <PermissionGuard permission="proposals"><ProposalListPage /></PermissionGuard> },
          { path: ':id', element: <PermissionGuard permission="proposals"><ProposalDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'equipment',
        children: [
          { element: <PermissionGuard permission="equipments"><EquipmentListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="equipments"><EquipmentDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'auditions',
        children: [
          { element: <PermissionGuard permission="auditions"><AuditionsListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="auditions"><AuditionDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'clients',
        children: [
          { element: <PermissionGuard permission="clients"><ClientsListPage permissions={{check:true}}/></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="clients"><ClientDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'geo',
        children: [
          { element: <PermissionGuard permission="geo"><GeoCountriesPage /></PermissionGuard>, index: true },
          { path: 'countries', element: <PermissionGuard permission="geo"><GeoCountriesPage /></PermissionGuard> },
          { path: 'states', element: <PermissionGuard permission="geo"><GeoStatesPage /></PermissionGuard> },
          { path: 'cities', element: <PermissionGuard permission="geo"><GeoCitiesPage /></PermissionGuard> },
          { path: 'tax-rates', element: <PermissionGuard permission="geo"><GeoTaxRatesPage /></PermissionGuard> },
        ]
      },
      {
        path: 'services',
        children: [
          { element: <PermissionGuard permission="services"><ServicesListPage /></PermissionGuard>, index: true },
          { path: 'new', element: <PermissionGuard permission="services"><ServicesNewPage /></PermissionGuard> },
        ]
      },
      {
        path: 'languages',
        children: [
          { element: <PermissionGuard permission="languages"><LanguagesListPage /></PermissionGuard>, index: true },
        ]
      },
      {
        path: 'contact-forms',
        children: [
          { element: <PermissionGuard permission="contactForms"><ContactFormsListPage /></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="contactForms"><ContactFormDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'testimonials',
        children: [
          { element: <PermissionGuard permission="testimonials"><TestimonialListPage /></PermissionGuard>, index: true },
          { path: 'new', element: <PermissionGuard permission="testimonials"><TestimonialNewPage /></PermissionGuard> },
          { path: ':id/edit', element: <PermissionGuard permission="testimonials"><TestimonialEditPage /></PermissionGuard> },
        ]
      },
      {
        path: 'contracts',
        children: [
          { element: <PermissionGuard permission="contracts"><ContractListPage /></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="contracts"><ContractDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'shared-media',
        element: (
          <PermissionGuard permission="sharedMedia">
            <SharedMediaListPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'access-requests',
        element: (
          <PermissionGuard permission="accessRequests">
            <AccessRequestsListPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'projects',
        children: [
          { element: <PermissionGuard permission="projects"><ProjectListPage /></PermissionGuard>, index: true },
          { path: ':id', element: <PermissionGuard permission="projects"><ProjectDetailsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'campaigns',
        children: [
          { element: <PermissionGuard permission="campaigns.email"><EmailCampaignPage /></PermissionGuard>, index: true },
          { path: 'email', element: <PermissionGuard permission="campaigns.email"><EmailCampaignPage /></PermissionGuard> },
          { path: 'whatsapp', element: <PermissionGuard permission="campaigns.whatsapp"><WhatsappCampaignPage /></PermissionGuard> },
          { path: 'leads', element: <PermissionGuard permission="campaigns.whatsapp"><CampaignLeadsPage /></PermissionGuard> },
        ]
      },
      {
        path: 'bookings',
        element: <PermissionGuard permission="bookings"><BookingsListPage /></PermissionGuard>,
      },
      // AI likeness licensing (staff side). Gated on `users`, matching the
      // backend's checkPermission('users') on /api/admin/likeness/*.
      {
        path: 'likeness',
        children: [
          { element: <PermissionGuard permission="users"><LikenessOptInsPage /></PermissionGuard>, index: true },
          { path: 'opt-ins', element: <PermissionGuard permission="users"><LikenessOptInsPage /></PermissionGuard> },
          { path: 'terms', element: <PermissionGuard permission="users"><LikenessTermsPage /></PermissionGuard> },
          { path: 'package', element: <PermissionGuard permission="users"><LikenessPackagePage /></PermissionGuard> },
        ]
      },
      {
        path: 'account-deletions',
        element: <PermissionGuard permission="accountDeletions"><AccountDeletionsPage /></PermissionGuard>,
      },
      {
        path: 'job-leads',
        // NOTE: :id must be registered alongside 'scrape-jobs' — react-router
        // matches the more specific static path automatically.
        children: [
          { element: <PermissionGuard permission="jobLeads.leads"><JobLeadsListPage /></PermissionGuard>, index: true },
          { path: 'scrape-jobs', element: <PermissionGuard permission="jobLeads.brightData"><BrightDataScrapeJobsListPage /></PermissionGuard> },
          { path: ':id', element: <PermissionGuard permission="jobLeads.leads"><JobLeadDetailsPage /></PermissionGuard> },
        ]
      }
    ],
  },
];
